/**
 * Real-time token migration flow.
 *
 * Contract addresses remain bundle/config authorities. The presentation below
 * makes the two signatures explicit without changing those security guards.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { formatEther, formatUnits, parseUnits, type Address, type Hex } from "viem";

import { browserPublicClient } from "@/lib/chain.client";
import { checkClaimContract, checkLegacyToken } from "@/lib/claim-guards";
import { erc20Abi, swapAbi } from "@/lib/contracts";
import {
  ASETQU_TOKEN,
  CHAIN_ID,
  CLAIM_CONTRACT,
  LEGACY_TOKEN,
  NETWORK_NAME,
  TOKEN_DECIMALS,
  TOKEN_SYMBOL,
  explorerAddress,
  explorerTx,
} from "@/lib/config";
import { describeTxError } from "@/lib/errors";
import { formatDisplay, formatTimestamp, shortenAddress } from "@/lib/format";

import { ConnectButton } from "./ConnectButton";
import { useWallet } from "./WalletProvider";
import {
  Alert,
  Button,
  ContractIdentity,
  CopyAction,
  MigrationRail,
  ProofStamp,
  ReviewRow,
  TransactionPlate,
} from "./ui";

type SwapInfo = {
  isDenied: boolean;
  legacyBalanceRaw: string;
  allowanceRaw: string;
  walletCapRaw: string;
  alreadySwappedRaw: string;
  remainingCapRaw: string;
  maxSwappableRaw: string;
};

type Eligibility = {
  address: string;
  claimMode: "swap";
  eligible: boolean;
  claimable: boolean;
  claimPaused: boolean | null;
  claimWindow: { start: string; end: string } | null;
  remainingRaw: string | null;
  currentBalanceRaw: string | null;
  contracts: { claim: string | null; asetquToken: string | null; legacyToken: string | null };
  swap: SwapInfo | null;
  chainError: string | null;
};

type Phase =
  | "idle"
  | "loading"
  | "estimating-approve"
  | "signing-approve"
  | "pending-approve"
  | "estimating-swap"
  | "signing-swap"
  | "pending-swap"
  | "done"
  | "error";

async function fetchEligibility(target: Address): Promise<Eligibility | null> {
  try {
    const response = await fetch(`/api/eligibility/${target}`, { cache: "no-store" });
    return (await response.json()) as Eligibility;
  } catch {
    return null;
  }
}

function ClaimFrame({
  currentStep,
  completeThrough,
  children,
}: {
  currentStep: number;
  completeThrough?: number;
  children: ReactNode;
}) {
  return (
    <div className="migration-workspace">
      <MigrationRail currentStep={currentStep} completeThrough={completeThrough} />
      <div className="migration-workspace__main">{children}</div>
    </div>
  );
}

export function ClaimCard() {
  const {
    address,
    isCorrectNetwork,
    error: walletError,
    networkSwitchUrl,
    switchNetwork,
    switchingNetwork,
  } = useWallet();

  if (!address) {
    return (
      <ClaimFrame currentStep={1} completeThrough={0}>
        <TransactionPlate
          step="01 / WALLET"
          title="HUBUNGKAN WALLET"
          description="Hubungkan wallet yang menyimpan AsetQu Legacy. Portal hanya membaca alamat dan saldo publik."
        >
          <div className="wallet-stage">
            <div>
              <p className="terminal-label">STATUS</p>
              <strong>BELUM TERHUBUNG</strong>
            </div>
            <ConnectButton />
          </div>
          <div className="signature-model">
            <div>
              <span>1</span>
              <strong>IZIN TOKEN</strong>
              <p>Exact amount. Belum memindahkan token.</p>
            </div>
            <div aria-hidden>→</div>
            <div>
              <span>2</span>
              <strong>MIGRASI</strong>
              <p>Memindahkan token legacy dan menerima token baru.</p>
            </div>
          </div>
          {CLAIM_CONTRACT ? (
            <ContractIdentity
              label="OFFICIAL MIGRATION CONTRACT"
              address={CLAIM_CONTRACT}
              href={explorerAddress(CLAIM_CONTRACT)}
            />
          ) : null}
        </TransactionPlate>
      </ClaimFrame>
    );
  }

  if (!isCorrectNetwork) {
    return (
      <ClaimFrame currentStep={1} completeThrough={0}>
        <TransactionPlate
          step="01 / WALLET"
          title="NETWORK CHANGE REQUIRED"
          description={`Wallet terhubung, tetapi migrasi hanya berlangsung di ${NETWORK_NAME}.`}
          tone="warning"
        >
          <dl className="review-ledger">
            <ReviewRow label="Wallet"><code>{address}</code></ReviewRow>
            <ReviewRow label="Jaringan yang diperlukan" emphasis>{NETWORK_NAME}</ReviewRow>
            <ReviewRow label="Chain ID">{CHAIN_ID}</ReviewRow>
          </dl>
          {walletError ? <Alert tone="warn" title="WALLET BELUM BERPINDAH">{walletError}</Alert> : null}
          <div className="transaction-actions">
            {networkSwitchUrl ? (
              <a className="ui-button ui-button--primary" href={networkSwitchUrl}>
                BUKA DI TRUST WALLET
              </a>
            ) : (
              <Button onClick={switchNetwork} disabled={switchingNetwork}>
                {switchingNetwork ? "MENUNGGU WALLET…" : "PINDAH KE BNB CHAIN"}
              </Button>
            )}
          </div>
        </TransactionPlate>
      </ClaimFrame>
    );
  }

  return <ClaimPanel key={address} address={address} />;
}

function ClaimPanel({ address }: { address: Address }) {
  const { getWalletClient, getProvider } = useWallet();

  const [eligibility, setEligibility] = useState<Eligibility | null>(null);
  const [phase, setPhase] = useState<Phase>("loading");
  const [message, setMessage] = useState<string | null>(null);
  const [gasEstimate, setGasEstimate] = useState<string | null>(null);
  const [txHash, setTxHash] = useState<Hex | null>(null);
  const [confirmedBlock, setConfirmedBlock] = useState<bigint | null>(null);
  const [confirmedTimestamp, setConfirmedTimestamp] = useState<bigint | null>(null);
  const [amountInput, setAmountInput] = useState("");

  const refresh = useCallback(async () => {
    const data = await fetchEligibility(address);
    if (data) {
      setEligibility(data);
      return data;
    }
    return null;
  }, [address]);

  useEffect(() => {
    let cancelled = false;

    void fetchEligibility(address).then((data) => {
      if (cancelled) return;
      if (data) {
        setEligibility(data);
        setPhase("idle");
        if (data.swap) {
          setAmountInput(formatUnits(BigInt(data.swap.maxSwappableRaw), TOKEN_DECIMALS));
        }
      } else {
        setPhase("error");
        setMessage("Portal tidak dapat membaca jaringan. Belum ada transaksi yang dikirim.");
      }
    });

    return () => {
      cancelled = true;
    };
  }, [address]);

  const amountRaw = useMemo<bigint | null>(() => {
    if (amountInput.trim() === "") return null;
    try {
      const parsed = parseUnits(amountInput.trim(), TOKEN_DECIMALS);
      return parsed > 0n ? parsed : null;
    } catch {
      return null;
    }
  }, [amountInput]);

  const maxSwappable = eligibility?.swap ? BigInt(eligibility.swap.maxSwappableRaw) : null;
  const allowance = eligibility?.swap ? BigInt(eligibility.swap.allowanceRaw) : null;
  const amountExceedsMax = amountRaw !== null && maxSwappable !== null && amountRaw > maxSwappable;
  const needsApproval = amountRaw !== null && allowance !== null ? allowance < amountRaw : null;
  const amountLabel = amountRaw === null ? "0" : formatDisplay(amountRaw);

  const runApprove = useCallback(async () => {
    if (!eligibility?.swap || amountRaw === null || !CLAIM_CONTRACT) return;
    setMessage(null);
    setTxHash(null);
    setPhase("estimating-approve");
    try {
      const legacyProblem = checkLegacyToken(LEGACY_TOKEN, eligibility.contracts.legacyToken);
      if (legacyProblem) throw new Error(legacyProblem);
      const contractProblem = checkClaimContract(CLAIM_CONTRACT, eligibility.contracts.claim);
      if (contractProblem) throw new Error(contractProblem);
      if (!LEGACY_TOKEN) throw new Error("Kontrak token legacy belum dikonfigurasi.");

      const walletClient = getWalletClient();
      const { request } = await browserPublicClient.simulateContract({
        address: LEGACY_TOKEN,
        abi: erc20Abi,
        functionName: "approve",
        args: [CLAIM_CONTRACT, amountRaw],
        account: address,
      });
      const gas = await browserPublicClient.estimateContractGas({
        address: LEGACY_TOKEN,
        abi: erc20Abi,
        functionName: "approve",
        args: [CLAIM_CONTRACT, amountRaw],
        account: address,
      });
      const gasPrice = await browserPublicClient.getGasPrice();
      setGasEstimate(`${Number(formatEther(gas * gasPrice)).toFixed(6)} BNB`);

      setPhase("signing-approve");
      const hash = await walletClient.writeContract({ ...request, account: address });
      setTxHash(hash);
      setPhase("pending-approve");

      const receipt = await browserPublicClient.waitForTransactionReceipt({ hash });
      if (receipt.status === "success") {
        await refresh();
        setPhase("idle");
        setTxHash(null);
        setGasEstimate(null);
      } else {
        setPhase("error");
        setMessage("Izin token gagal dikonfirmasi. Tidak ada token yang berpindah.");
      }
    } catch (error) {
      setPhase("error");
      setMessage(describeTxError(error));
    }
  }, [address, amountRaw, eligibility, getWalletClient, refresh]);

  const runSwap = useCallback(async () => {
    if (amountRaw === null || !CLAIM_CONTRACT || !eligibility) return;
    setMessage(null);
    setPhase("estimating-swap");
    try {
      const contractProblem = checkClaimContract(CLAIM_CONTRACT, eligibility.contracts.claim);
      if (contractProblem) throw new Error(contractProblem);

      const walletClient = getWalletClient();
      const { request } = await browserPublicClient.simulateContract({
        address: CLAIM_CONTRACT,
        abi: swapAbi,
        functionName: "swap",
        args: [amountRaw],
        account: address,
      });
      const gas = await browserPublicClient.estimateContractGas({
        address: CLAIM_CONTRACT,
        abi: swapAbi,
        functionName: "swap",
        args: [amountRaw],
        account: address,
      });
      const gasPrice = await browserPublicClient.getGasPrice();
      setGasEstimate(`${Number(formatEther(gas * gasPrice)).toFixed(6)} BNB`);

      setPhase("signing-swap");
      const hash = await walletClient.writeContract({ ...request, account: address });
      setTxHash(hash);
      setPhase("pending-swap");

      const receipt = await browserPublicClient.waitForTransactionReceipt({ hash });
      if (receipt.status === "success") {
        if (typeof receipt.blockNumber === "bigint") {
          setConfirmedBlock(receipt.blockNumber);
          try {
            const block = await browserPublicClient.getBlock({ blockNumber: receipt.blockNumber });
            setConfirmedTimestamp(block.timestamp);
          } catch {
            // The receipt is the source of truth; block time is supporting metadata.
          }
        }
        await refresh();
        setPhase("done");
        setGasEstimate(null);
      } else {
        setPhase("error");
        setMessage("Migrasi ditolak kontrak. Token legacy tidak berpindah.");
      }
    } catch (error) {
      setPhase("error");
      setMessage(describeTxError(error));
    }
  }, [address, amountRaw, eligibility, getWalletClient, refresh]);

  const addTokenToWallet = useCallback(async () => {
    if (!ASETQU_TOKEN) return;
    const provider = getProvider();
    if (!provider) return;
    try {
      await provider.request({
        method: "wallet_watchAsset",
        params: {
          type: "ERC20",
          options: { address: ASETQU_TOKEN, symbol: TOKEN_SYMBOL, decimals: TOKEN_DECIMALS },
        },
      });
    } catch {
      // The wallet can decline without changing the migration result.
    }
  }, [getProvider]);

  const setMax = useCallback(() => {
    if (!eligibility?.swap) return;
    setAmountInput(formatUnits(BigInt(eligibility.swap.maxSwappableRaw), TOKEN_DECIMALS));
  }, [eligibility]);

  const busy = [
    "estimating-approve",
    "signing-approve",
    "pending-approve",
    "estimating-swap",
    "signing-swap",
    "pending-swap",
  ].includes(phase);

  if (phase === "loading") {
    return (
      <ClaimFrame currentStep={2} completeThrough={1}>
        <TransactionPlate
          step="02 / KELAYAKAN"
          title="MEMERIKSA KONTRAK"
          description="Membaca saldo legacy, allowance, batas wallet, dan status jaringan."
        >
          <div className="terminal-loading" role="status" aria-live="polite">
            <span aria-hidden />
            <div>
              <strong>READING ON-CHAIN STATE</strong>
              <p>Belum ada keputusan atau transaksi yang dibuat.</p>
            </div>
          </div>
        </TransactionPlate>
      </ClaimFrame>
    );
  }

  const swap = eligibility?.swap;
  const isDenied = swap?.isDenied ?? false;
  const chainError = eligibility?.chainError;
  const legacyBalance = swap ? BigInt(swap.legacyBalanceRaw) : 0n;
  const alreadySwapped = swap ? BigInt(swap.alreadySwappedRaw) : 0n;
  const walletCap = swap ? BigInt(swap.walletCapRaw) : 0n;
  const currentStep = phase === "done" ? 5 : !swap || isDenied || maxSwappable === 0n ? 2 : needsApproval === false ? 4 : 3;
  const completeThrough = phase === "done" ? 4 : Math.max(1, currentStep - 1);
  const criticalMessage = message?.toLowerCase().includes("tidak cocok") ?? false;

  if (phase === "done" && txHash) {
    return (
      <ClaimFrame currentStep={5} completeThrough={4}>
        <ProofStamp
          title="MIGRASI TERKONFIRMASI"
          record={`TX ${shortenAddress(txHash, 10, 8)}`}
          actions={
            <>
              <a href={`/verify?tx=${txHash}`}>VERIFIKASI MIGRASI</a>
              <a href={explorerTx(txHash)} target="_blank" rel="noopener noreferrer">LIHAT TRANSAKSI ↗</a>
              <CopyAction value={txHash} label="Salin hash" />
              {ASETQU_TOKEN ? (
                <button type="button" onClick={() => void addTokenToWallet()}>
                  TAMBAHKAN {TOKEN_SYMBOL.toUpperCase()} KE WALLET
                </button>
              ) : null}
            </>
          }
        >
          <ReviewRow label="Wallet"><code>{address}</code></ReviewRow>
          <ReviewRow label="Legacy">{amountLabel} {TOKEN_SYMBOL}</ReviewRow>
          <ReviewRow label="Diterima" emphasis>{amountLabel} {TOKEN_SYMBOL}</ReviewRow>
          <ReviewRow label="Rasio">1 : 1</ReviewRow>
          <ReviewRow label="Network">{NETWORK_NAME}</ReviewRow>
          <ReviewRow label="Transaction"><code>{txHash}</code></ReviewRow>
          {confirmedBlock != null ? <ReviewRow label="Block">#{confirmedBlock.toString()}</ReviewRow> : null}
          {confirmedTimestamp !== null ? (
            <ReviewRow label="Waktu">{formatTimestamp(confirmedTimestamp)} WIB</ReviewRow>
          ) : null}
        </ProofStamp>
      </ClaimFrame>
    );
  }

  return (
    <ClaimFrame currentStep={currentStep} completeThrough={completeThrough}>
      <TransactionPlate
        step={`${String(currentStep).padStart(2, "0")} / ${currentStep === 2 ? "KELAYAKAN" : currentStep === 3 ? "IZIN TOKEN" : "MIGRASI"}`}
        title={currentStep === 2 ? "PERIKSA KELAYAKAN" : currentStep === 3 ? "BERI IZIN TOKEN" : "MIGRASIKAN TOKEN"}
        description={
          currentStep === 3
            ? "Ini adalah tanda tangan pertama. Wallet hanya memberi kontrak izin menggunakan exact amount; token belum berpindah."
            : currentStep === 4
              ? "Ini adalah tanda tangan kedua. AsetQu Legacy dipindahkan dan wallet yang sama menerima AsetQu baru."
              : "Hasil pemeriksaan wallet dan kontrak resmi."
        }
        tone={criticalMessage ? "danger" : "default"}
      >
        <div className="wallet-identity">
          <div>
            <p>WALLET CONNECTED</p>
            <code>{address}</code>
          </div>
          <strong>{NETWORK_NAME}</strong>
        </div>

        {chainError ? <Alert tone="warn" title="NETWORK UNAVAILABLE">{chainError}</Alert> : null}

        {isDenied ? (
          <Alert tone="danger" title="WALLET DIBATASI">
            Wallet ini tidak dapat menggunakan kontrak migrasi. Jangan mencoba menandatangani
            transaksi dari tautan atau kontrak lain.
          </Alert>
        ) : null}

        {!swap && !chainError ? (
          <Alert tone="warn" title="DATA KELAYAKAN BELUM TERSEDIA">
            Portal belum menerima data saldo dari kontrak. Coba muat ulang setelah jaringan stabil.
          </Alert>
        ) : null}

        {swap && !isDenied ? (
          <>
            <dl className="eligibility-ledger">
              <ReviewRow label="Saldo legacy" emphasis>{formatDisplay(legacyBalance)} {TOKEN_SYMBOL}</ReviewRow>
              <ReviewRow label="Sudah dimigrasikan">{formatDisplay(alreadySwapped)} / {formatDisplay(walletCap)}</ReviewRow>
              <ReviewRow label="Dapat dimigrasikan">{maxSwappable === null ? "—" : `${formatDisplay(maxSwappable)} ${TOKEN_SYMBOL}`}</ReviewRow>
              <ReviewRow label="Rasio">1 LEGACY = 1 NEW</ReviewRow>
            </dl>

            {maxSwappable !== null && maxSwappable > 0n ? (
              <div className="amount-control">
                <label htmlFor="swap-amount">JUMLAH YANG DIMIGRASIKAN</label>
                <div>
                  <input
                    id="swap-amount"
                    type="text"
                    inputMode="decimal"
                    value={amountInput}
                    onChange={(event) => setAmountInput(event.target.value)}
                    disabled={busy}
                    placeholder="0.0"
                  />
                  <span>{TOKEN_SYMBOL}</span>
                  <Button variant="secondary" onClick={setMax} disabled={busy}>MAKS</Button>
                </div>
                {amountExceedsMax ? (
                  <p role="alert">Jumlah melebihi batas saat ini: {formatDisplay(maxSwappable)} {TOKEN_SYMBOL}.</p>
                ) : null}
              </div>
            ) : (
              <Alert tone="info" title="TIDAK ADA SALDO LEGACY YANG DAPAT DIMIGRASIKAN">
                Wallet ini tidak memiliki saldo yang tersedia untuk migrasi, atau batas wallet sudah tercapai.
              </Alert>
            )}

            {maxSwappable !== null && maxSwappable > 0n && CLAIM_CONTRACT ? (
              <>
                <div className="transaction-review">
                  <p className="terminal-label">TRANSACTION REVIEW</p>
                  <dl className="review-ledger">
                    <ReviewRow label="Token">ASETQU LEGACY</ReviewRow>
                    <ReviewRow label="Jumlah" emphasis>{amountLabel} {TOKEN_SYMBOL}</ReviewRow>
                    <ReviewRow label={currentStep === 3 ? "Izin" : "Hasil"}>
                      {currentStep === 3 ? "EXACT AMOUNT ONLY" : `${amountLabel} ${TOKEN_SYMBOL} BARU`}
                    </ReviewRow>
                    <ReviewRow label={currentStep === 3 ? "Kontrak penerima izin" : "Penerima"}>
                      <code>{currentStep === 3 ? CLAIM_CONTRACT : address}</code>
                    </ReviewRow>
                    <ReviewRow label="Network">{NETWORK_NAME}</ReviewRow>
                    <ReviewRow label="Perkiraan network fee">{gasEstimate ?? "Dihitung sebelum wallet dibuka"}</ReviewRow>
                  </dl>
                </div>

                <ContractIdentity
                  label={currentStep === 3 ? "CONTRACT RECEIVING PERMISSION" : "OFFICIAL MIGRATION CONTRACT"}
                  address={CLAIM_CONTRACT}
                  href={explorerAddress(CLAIM_CONTRACT)}
                />

                <div className="signing-check">
                  <p>CHECK BEFORE SIGNING</p>
                  <ul>
                    <li><span aria-hidden>✓</span> Network: {NETWORK_NAME}</li>
                    <li><span aria-hidden>✓</span> Token: AsetQu Legacy</li>
                    <li><span aria-hidden>✓</span> Amount: {amountLabel} {TOKEN_SYMBOL}</li>
                    <li><span aria-hidden>✓</span> Contract: {shortenAddress(CLAIM_CONTRACT, 8, 6)}</li>
                  </ul>
                  <strong>Jangan tanda tangani jika detail di wallet berbeda.</strong>
                </div>
              </>
            ) : null}
          </>
        ) : null}

        {eligibility?.claimPaused ? (
          <Alert tone="warn" title="MIGRASI DIJEDA">
            Kontrak sedang dijeda. Tidak ada dana yang terpengaruh dan CTA dinonaktifkan.
          </Alert>
        ) : null}

        {eligibility?.claimWindow ? (
          <p className="migration-window">
            PERIODE MIGRASI BERAKHIR <strong>{formatTimestamp(BigInt(eligibility.claimWindow.end))} WIB</strong>
          </p>
        ) : null}

        {phase === "signing-approve" || phase === "signing-swap" ? (
          <div className="wallet-waiting" role="status" aria-live="polite">
            <span aria-hidden />
            <div>
              <strong>WAITING FOR WALLET</strong>
              <p>Periksa jumlah, kontrak, dan jaringan di wallet Anda.</p>
            </div>
          </div>
        ) : null}

        {txHash ? (
          <div className="transaction-submitted" role="status" aria-live="polite">
            <div>
              <p>{phase === "pending-approve" ? "TOKEN PERMISSION SUBMITTED" : "MIGRATION SUBMITTED"}</p>
              <strong>MENUNGGU KONFIRMASI JARINGAN</strong>
            </div>
            <code>{txHash}</code>
            <a href={explorerTx(txHash)} target="_blank" rel="noopener noreferrer">LIHAT TRANSAKSI ↗</a>
          </div>
        ) : null}

        {message ? (
          <Alert tone={criticalMessage ? "danger" : "warn"} title={criticalMessage ? "TRANSAKSI DIBLOKIR" : "TINDAKAN BELUM SELESAI"}>
            {message}
          </Alert>
        ) : null}

        {swap && !isDenied && maxSwappable !== null && maxSwappable > 0n ? (
          <div className="transaction-actions">
            {needsApproval !== false ? (
              <Button
                onClick={() => void runApprove()}
                disabled={!eligibility?.claimable || amountRaw === null || amountExceedsMax || busy}
              >
                {phase === "estimating-approve"
                  ? "MENGHITUNG NETWORK FEE…"
                  : phase === "signing-approve"
                    ? "PERIKSA WALLET ANDA…"
                    : phase === "pending-approve"
                      ? "IZIN SEDANG DIKONFIRMASI…"
                      : `BERI IZIN ${amountLabel} ${TOKEN_SYMBOL.toUpperCase()}`}
              </Button>
            ) : (
              <Button
                onClick={() => void runSwap()}
                disabled={!eligibility?.claimable || amountRaw === null || amountExceedsMax || busy}
              >
                {phase === "estimating-swap"
                  ? "MENGHITUNG NETWORK FEE…"
                  : phase === "signing-swap"
                    ? "PERIKSA WALLET ANDA…"
                    : phase === "pending-swap"
                      ? "MIGRASI SEDANG DIKONFIRMASI…"
                      : `MIGRASIKAN ${amountLabel} ${TOKEN_SYMBOL.toUpperCase()}`}
              </Button>
            )}
            <p>
              Wallet akan meminta tanda tangan {needsApproval !== false ? "pertama" : "kedua"} dari dua transaksi.
            </p>
          </div>
        ) : null}
      </TransactionPlate>
    </ClaimFrame>
  );
}
